// Generated from products/analytics/sdk/browserSignals.ts; run analytics generate:site-runtime.
/** A bounded, best-effort snapshot of browser capabilities. */
export interface BrowserSignals {
  browser: {
    userAgent: string | null;
    vendor: string | null;
    platform: string | null;
    languages: string[];
    cookieEnabled: boolean | null;
    online: boolean | null;
    maxTouchPoints: number | null;
    webdriver: boolean | null;
    userAgentData: {
      brands: Array<{ brand: string; version: string }>;
      mobile: boolean | null;
      platform: string | null;
    } | null;
  };
  hardware: { logicalCores: number | null; deviceMemoryGiB: number | null };
  display: { pixelRatio: number | null; colorDepth: number | null; orientation: string | null };
  preferences: {
    colorScheme: "dark" | "light" | "no-preference" | null;
    reducedMotion: boolean | null;
    contrast: "more" | "less" | "custom" | "no-preference" | null;
  };
  network: {
    effectiveType: "slow-2g" | "2g" | "3g" | "4g" | null;
    downlinkMbps: number | null;
    rttMs: number | null;
    saveData: boolean | null;
  };
  graphics: { version: "webgl" | "webgl2" | null; vendor: string | null; renderer: string | null };
  fonts: string[];
  media: {
    audio: Record<AudioCodec, CodecSupport>;
    video: Record<VideoCodec, CodecSupport>;
  };
}

export type AudioCodec = "mpeg" | "vorbis" | "aac" | "opus";
export type VideoCodec = "h264" | "vp8" | "vp9" | "av1";
export type CodecSupport = "probably" | "maybe" | "unsupported" | "unavailable";

/** Injection seam for deterministic tests and non-window embedders. */
export interface BrowserSignalsEnvironment {
  navigator?: () => Navigator | undefined;
  window?: () => Window | undefined;
  document?: () => Document | undefined;
  createCanvas?: () => HTMLCanvasElement | undefined;
}

const MAX_TEXT = 128;
const MAX_LANGUAGES = 8;
const MAX_BRANDS = 8;
const MAX_FONTS = 12;
const WEBGL_OPTIONS: WebGLContextAttributes = {
  alpha: false,
  antialias: false,
  depth: false,
  failIfMajorPerformanceCaveat: true,
  powerPreference: "low-power",
  premultipliedAlpha: false,
  preserveDrawingBuffer: false,
  stencil: false,
};

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value.slice(0, MAX_TEXT) : null;
}

function read<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function property(target: unknown, key: string): unknown {
  if (!target || (typeof target !== "object" && typeof target !== "function")) return undefined;
  return read(() => (target as Record<string, unknown>)[key]);
}

function stringProperty(target: unknown, key: string): string | null {
  return text(property(target, key));
}

function booleanProperty(target: unknown, key: string): boolean | null {
  const value = property(target, key);
  return typeof value === "boolean" ? value : null;
}

function boundedNumber(value: unknown, min: number, max: number, decimals = 0): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < min) return null;
  const scale = 10 ** decimals;
  return Math.min(max, Math.round(value * scale) / scale);
}

function numberProperty(
  target: unknown,
  key: string,
  min: number,
  max: number,
  decimals = 0,
): number | null {
  return boundedNumber(property(target, key), min, max, decimals);
}

function choice<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : null;
}

function listLanguages(nav: Navigator | undefined): string[] {
  const raw = read(() => nav?.languages);
  if (!raw) return [];
  const result: string[] = [];
  try {
    if (typeof raw[Symbol.iterator] !== "function") return [];
    let inspected = 0;
    for (const item of raw) {
      if (++inspected > 64) break;
      const value = text(item);
      if (value && !result.includes(value)) result.push(value);
      if (result.length >= MAX_LANGUAGES) break;
    }
  } catch {
    return result;
  }
  return result;
}

function userAgentData(nav: Navigator | undefined): BrowserSignals["browser"]["userAgentData"] {
  const data = read(
    () => (nav as (Navigator & { userAgentData?: unknown }) | undefined)?.userAgentData,
  );
  if (!data || typeof data !== "object") return null;
  const brands: Array<{ brand: string; version: string }> = [];
  const rawBrands = read(() => (data as { brands?: unknown }).brands);
  if (rawBrands) {
    try {
      if (typeof (rawBrands as Iterable<unknown>)[Symbol.iterator] !== "function") {
        return {
          brands,
          mobile: booleanProperty(data, "mobile"),
          platform: stringProperty(data, "platform"),
        };
      }
      let inspected = 0;
      for (const item of rawBrands as Iterable<unknown>) {
        if (++inspected > 64) break;
        if (!item || typeof item !== "object") continue;
        const brand = text(read(() => (item as { brand?: unknown }).brand));
        const version = text(read(() => (item as { version?: unknown }).version));
        if (brand && version) brands.push({ brand, version });
        if (brands.length >= MAX_BRANDS) break;
      }
    } catch {
      // Preserve any brands already read.
    }
  }
  return {
    brands,
    mobile: booleanProperty(data, "mobile"),
    platform: stringProperty(data, "platform"),
  };
}

function mediaMatch(win: Window | undefined, query: string): boolean | null {
  const result = read(() => win?.matchMedia(query).matches);
  return typeof result === "boolean" ? result : null;
}

function readGraphics(
  env: BrowserSignalsEnvironment,
  doc: Document | undefined,
): BrowserSignals["graphics"] {
  let canvas: HTMLCanvasElement | undefined;
  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  let version: "webgl" | "webgl2" | null = null;
  try {
    canvas = env.createCanvas ? env.createCanvas() : doc?.createElement("canvas");
    if (!canvas) return { version, vendor: null, renderer: null };
    gl = canvas.getContext("webgl2", WEBGL_OPTIONS) as WebGL2RenderingContext | null;
    if (gl) version = "webgl2";
    else {
      gl = canvas.getContext("webgl", WEBGL_OPTIONS) as WebGLRenderingContext | null;
      if (gl) version = "webgl";
    }
    if (!gl) return { version, vendor: null, renderer: null };
    const debugInfo = read(() => gl?.getExtension("WEBGL_debug_renderer_info")) as
      | { UNMASKED_VENDOR_WEBGL?: number; UNMASKED_RENDERER_WEBGL?: number }
      | null
      | undefined;
    const vendorEnum = debugInfo?.UNMASKED_VENDOR_WEBGL;
    const rendererEnum = debugInfo?.UNMASKED_RENDERER_WEBGL;
    return {
      version,
      vendor: text(read(() => gl?.getParameter(vendorEnum ?? gl!.VENDOR))),
      renderer: text(read(() => gl?.getParameter(rendererEnum ?? gl!.RENDERER))),
    };
  } catch {
    return { version, vendor: null, renderer: null };
  } finally {
    try {
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      // Context cleanup is best effort on hostile or partial implementations.
    }
  }
}

function readFonts(doc: Document | undefined): string[] {
  const faces = read(() => doc?.fonts);
  if (!faces) return [];
  const result: string[] = [];
  try {
    let inspected = 0;
    for (const face of faces) {
      if (++inspected > 64) break;
      if (face.status !== "loaded") continue;
      const family = text(face.family.replace(/^['"]|['"]$/g, ""));
      if (family && !result.includes(family)) result.push(family);
      if (result.length >= MAX_FONTS) break;
    }
  } catch {
    // Keep the bounded prefix if enumeration fails part way through.
  }
  return result;
}

function codecs(
  element: HTMLMediaElement | undefined,
  definitions: Record<string, string>,
): Record<string, CodecSupport> {
  const result: Record<string, CodecSupport> = {};
  for (const [name, mime] of Object.entries(definitions)) {
    if (!element) {
      result[name] = "unavailable";
      continue;
    }
    const value = read(() => element.canPlayType(mime));
    result[name] =
      value === "probably" || value === "maybe"
        ? value
        : value === ""
          ? "unsupported"
          : "unavailable";
  }
  return result;
}

function readMedia(doc: Document | undefined): BrowserSignals["media"] {
  let audio: HTMLAudioElement | undefined;
  let video: HTMLVideoElement | undefined;
  try {
    audio = doc?.createElement("audio");
  } catch {
    // Report codec probes as unavailable.
  }
  try {
    video = doc?.createElement("video");
  } catch {
    // Report codec probes as unavailable.
  }
  return {
    audio: codecs(audio, {
      mpeg: 'audio/mpeg; codecs="mp3"',
      vorbis: 'audio/ogg; codecs="vorbis"',
      aac: 'audio/mp4; codecs="mp4a.40.2"',
      opus: 'audio/webm; codecs="opus"',
    }) as BrowserSignals["media"]["audio"],
    video: codecs(video, {
      h264: 'video/mp4; codecs="avc1.42E01E"',
      vp8: 'video/webm; codecs="vp8"',
      vp9: 'video/webm; codecs="vp9"',
      av1: 'video/mp4; codecs="av01.0.05M.08"',
    }) as BrowserSignals["media"]["video"],
  };
}

/** Read one bounded snapshot. Callers should cache it for the client's lifetime. */
export function readBrowserSignals(environment: BrowserSignalsEnvironment = {}): BrowserSignals {
  const nav = read(() =>
    environment.navigator
      ? environment.navigator()
      : typeof navigator === "undefined"
        ? undefined
        : navigator,
  );
  const win = read(() =>
    environment.window ? environment.window() : typeof window === "undefined" ? undefined : window,
  );
  const doc = read(() =>
    environment.document
      ? environment.document()
      : typeof document === "undefined"
        ? undefined
        : document,
  );
  const connection = read(
    () => (nav as (Navigator & { connection?: unknown }) | undefined)?.connection,
  );
  const screen = read(() => win?.screen);
  const orientation = read(() => screen?.orientation?.type);
  const hardware = {
    logicalCores: numberProperty(nav, "hardwareConcurrency", 1, 256),
    deviceMemoryGiB: boundedNumber(
      read(() => (nav as (Navigator & { deviceMemory?: unknown }) | undefined)?.deviceMemory),
      0.25,
      64,
      2,
    ),
  };
  const mediaPreferences = {
    colorScheme:
      mediaMatch(win, "(prefers-color-scheme: dark)") === true
        ? ("dark" as const)
        : mediaMatch(win, "(prefers-color-scheme: light)") === true
          ? ("light" as const)
          : mediaMatch(win, "(prefers-color-scheme: no-preference)") === true
            ? ("no-preference" as const)
            : null,
    reducedMotion: mediaMatch(win, "(prefers-reduced-motion: reduce)"),
    contrast: ((): BrowserSignals["preferences"]["contrast"] => {
      for (const value of ["more", "less", "custom", "no-preference"] as const) {
        if (mediaMatch(win, `(prefers-contrast: ${value})`) === true) return value;
      }
      return null;
    })(),
  };
  return {
    browser: {
      userAgent: stringProperty(nav, "userAgent"),
      vendor: stringProperty(nav, "vendor"),
      platform: stringProperty(nav, "platform"),
      languages: listLanguages(nav),
      cookieEnabled: booleanProperty(nav, "cookieEnabled"),
      online: booleanProperty(nav, "onLine"),
      maxTouchPoints: numberProperty(nav, "maxTouchPoints", 0, 256),
      webdriver: booleanProperty(nav, "webdriver"),
      userAgentData: userAgentData(nav),
    },
    hardware,
    display: {
      pixelRatio: numberProperty(win, "devicePixelRatio", 0, 16, 2),
      colorDepth: numberProperty(screen, "colorDepth", 0, 128),
      orientation: choice(orientation, [
        "portrait-primary",
        "portrait-secondary",
        "landscape-primary",
        "landscape-secondary",
      ]),
    },
    preferences: {
      colorScheme: mediaPreferences.colorScheme,
      reducedMotion: mediaPreferences.reducedMotion,
      contrast: mediaPreferences.contrast,
    },
    network: {
      effectiveType: choice(stringProperty(connection, "effectiveType"), [
        "slow-2g",
        "2g",
        "3g",
        "4g",
      ]),
      downlinkMbps: numberProperty(connection, "downlink", 0, 1000, 2),
      rttMs: numberProperty(connection, "rtt", 0, 600_000),
      saveData: booleanProperty(connection, "saveData"),
    },
    graphics: readGraphics(environment, doc),
    fonts: readFonts(doc),
    media: readMedia(doc),
  };
}
